import { Component, inject, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NormsService } from '../services/norms.service';
import { EstimationService } from '../services/estimation.service';
import { AppInsightsService } from '../services/app-insights.service';
import { NormsCacheService } from '../services/norms-cache.service';
import { NormsLookupResponse, NormsEstimate, Craft } from '../models/norms.model';
import { JOB_COMPLEXITY, TOGGLE_MULTIPLIERS, ELEVATED_WORK_OPTIONS, OPTION_LISTS, PRESSURE_CLASS_MAP, TUBING_TIERS, TASK_TAXONOMY, TaxCategory, TaxSubCat } from '../config/estimation-config';

type TaskCategory = 'motor' | 'pump' | 'prd' | 'valve' | 'pipework' | 'electrical' | 'instrumentation' | 'rotating';

type CraftFilter = Craft | 'ALL';

interface PrdForm { size: string; action: string; pressureClass: string; }
interface OrificeForm { pipeSize: string; pressureClass: string; }
interface PumpScreenForm { pipeSize: string; pressureClass: string; }

@Component({
  selector: 'app-norms-lookup',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="page-wrapper">

      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1>General NORMs Task Lookup</h1>
          <p class="page-sub">Standard PM durations and crew sizes from the Arcadis NORMs library (updated {{ libraryUpdatedLabel }})</p>
        </div>
      </div>

      <!-- Two-column layout -->
      <div class="content-grid">

        <!-- LEFT: Lookup Parameters (card) -->
        <section class="params-panel card">

          <div class="panel-header">
            <h2 class="panel-title">Lookup Parameters</h2>
            <button class="btn btn-outline-primary btn-sm" *ngIf="isLocked" (click)="clearForm()">
              &#8635; New Task Lookup
            </button>
            <button class="btn btn-outline-primary btn-sm" *ngIf="!isLocked" (click)="clearForm()">Clear</button>
          </div>
          <!-- Lock overlay message -->
          <div class="panel-locked-notice" *ngIf="isLocked">
            Parameters locked. Click <strong>New Task Lookup</strong> to start a new search.
          </div>

          <!-- Task Category -->
          <div class="form-group" [class.jc-locked]="isLocked">
            <label class="form-label required">Task Category</label>

            <!-- Quick search across every task (jumps to the category + sub-task) -->
            <div class="task-search">
              <input
                type="text"
                class="task-search-input"
                [(ngModel)]="taskSearch"
                [disabled]="isLocked"
                placeholder="Search tasks — e.g. coupling, greasing, tubing…"
              >
              <button class="task-search-clear" *ngIf="taskSearch" [disabled]="isLocked" (click)="taskSearch = ''" aria-label="Clear search">&times;</button>
              <div class="task-search-results" *ngIf="searchResults().length">
                <button class="task-search-item" *ngFor="let r of searchResults()" (click)="selectSearchResult(r)">
                  <span class="tsi-sub">{{ r.subLabel }}</span>
                  <span class="tsi-cat">{{ r.catLabel }}</span>
                </button>
              </div>
              <div class="task-search-empty" *ngIf="taskSearch.length >= 2 && !searchResults().length">
                No matching tasks — try a different keyword.
              </div>
            </div>

            <!-- Categories grouped into 3 columns: I&E · Rotating · Fixed -->
            <div class="category-columns">
              <div class="category-col">
                <button class="cat-btn" [class.active]="category==='electrical'"      [disabled]="isLocked" (click)="setCategory('electrical')">Electrical</button>
                <button class="cat-btn" [class.active]="category==='instrumentation'" [disabled]="isLocked" (click)="setCategory('instrumentation')">Instrumentation</button>
              </div>
              <div class="category-col">
                <button class="cat-btn" [class.active]="category==='motor'"           [disabled]="isLocked" (click)="setCategory('motor')">Motor</button>
                <button class="cat-btn" [class.active]="category==='pump'"            [disabled]="isLocked" (click)="setCategory('pump')">Pump</button>
                <button class="cat-btn" [class.active]="category==='rotating'"        [disabled]="isLocked" (click)="setCategory('rotating')">General Rotating Equipment</button>
              </div>
              <div class="category-col">
                <button class="cat-btn" [class.active]="category==='prd'"             [disabled]="isLocked" (click)="setCategory('prd')">PRD</button>
                <button class="cat-btn" [class.active]="category==='valve'"           [disabled]="isLocked" (click)="setCategory('valve')">Control Valve</button>
                <button class="cat-btn" [class.active]="category==='pipework'"        [disabled]="isLocked" (click)="setCategory('pipework')">Pipework &amp; Flanges</button>
              </div>
            </div>
          </div>

          <!--  PRD sub-form  -->
          <div class="sub-form" [class.jc-locked]="isLocked" *ngIf="category === 'prd'">
            <div class="two-col">
              <div class="form-group">
                <label class="form-label required" for="prdSize">PRD Size</label>
                <select id="prdSize" [(ngModel)]="prd.size">
                  <option value="">Select...</option>
                  <option *ngFor="let s of prdSizes" [value]="s">{{ s }}</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label required" for="prdClass">Inlet Pressure Class</label>
                <select id="prdClass" [(ngModel)]="prd.pressureClass">
                  <option value="">Select...</option>
                  <option *ngFor="let c of pressureClasses" [value]="c">{{ c }}</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label required">PRD Action</label>
              <div class="toggle-group">
                <button class="toggle-btn" *ngFor="let a of prdActions"
                        [class.active]="prd.action === a"
                        (click)="prd.action = a">{{ a }}</button>
              </div>
            </div>
            <div class="query-preview" *ngIf="buildPrdQuery()">
              <span class="qp-label">Task query:</span>
              <span class="qp-value">{{ buildPrdQuery() }}</span>
            </div>
          </div>

          <!--  ORIFICE sub-form (legacy — retained for potential direct query use)  -->
          <!--  PUMP SCREEN sub-form (legacy — retained for potential direct query use)  -->

          <!--  GENERIC taxonomy sub-form: Motor / Pump / Valve / Pipework / Electrical / Instrumentation / Rotating  -->
          <div class="sub-form" [class.jc-locked]="isLocked" *ngIf="isGenericCategory()">

            <!-- Sub-category selector -->
            <div class="form-group" *ngIf="getActiveTaxCat() as taxCat">
              <label class="form-label required">Sub-Category</label>
              <div class="category-group">
                <button class="cat-btn cat-btn-sm"
                        *ngFor="let sc of taxCat.subCats"
                        [class.active]="selectedSubCat === sc.label"
                        [disabled]="isLocked"
                        (click)="setSubCat(sc.label)">
                  {{ sc.label }}
                </button>
              </div>
            </div>

            <!-- Pump/Motor sizing reference guide -->
            <div class="sizing-guide"
                 *ngIf="(category === 'motor' || category === 'pump') && showsSizeField()">
              <button class="sizing-guide-toggle" type="button"
                      (click)="showSizingChart = !showSizingChart">
                Pump/Motor Sizing Guide
                <span class="sizing-toggle-arrow">{{ showSizingChart ? '&#9650;' : '&#9660;' }}</span>
              </button>
              <div class="sizing-guide-body" *ngIf="showSizingChart">
                <p class="sizing-guide-note">Select the class that matches the motor HP or kW rating.</p>
                <table class="sizing-table">
                  <thead>
                    <tr>
                      <th>Class</th>
                      <th>HP Range</th>
                      <th>kW Range</th>
                      <th>Pipe</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr class="sz-small">
                      <td>Small</td><td>1&ndash;15 HP</td><td>0.75&ndash;11 kW</td><td>&lt;3&rdquo;</td>
                    </tr>
                    <tr class="sz-medium">
                      <td>Medium</td><td>16&ndash;100 HP</td><td>12&ndash;75 kW</td><td>3&rdquo;&ndash;6&rdquo;</td>
                    </tr>
                    <tr class="sz-large">
                      <td>Large</td><td>101&ndash;399 HP</td><td>76&ndash;299 kW</td><td>8&rdquo;&ndash;16&rdquo;</td>
                    </tr>
                    <tr class="sz-xlarge">
                      <td>X-Large</td><td>400+ HP</td><td>300+ kW</td><td>18&rdquo;&ndash;24&rdquo;</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Dynamic option fields for selected sub-category -->
            <ng-container *ngIf="getActiveSubCatDef() as subCat">
              <div class="form-group" *ngFor="let field of subCat.fields">
                <label class="form-label" [class.required]="field.required">{{ field.label }}</label>
                <div class="toggle-group flex-wrap">
                  <button class="toggle-btn"
                          *ngFor="let ch of field.choices"
                          [class.active]="subCatOptions[field.key] === ch"
                          [disabled]="isLocked"
                          (click)="setSubCatOption(field.key, ch)">
                    {{ ch }}
                  </button>
                </div>
              </div>

              <!-- Query preview -->
              <div class="query-preview" *ngIf="buildGenericQuery()">
                <span class="qp-label">Task query:</span>
                <span class="qp-value">{{ buildGenericQuery() }}</span>
              </div>
            </ng-container>
          </div>

          <!-- ── Job Conditions (NORMs multipliers only — Labor-Hours tab has none) ── -->
          <div class="jc-section" [class.jc-locked]="isLocked">
            <div class="jc-section-label">Job Conditions</div>

            <!-- Job Complexity -->
            <div class="form-group">
              <label class="form-label">Job Complexity</label>
              <div class="toggle-group">
                <button class="toggle-btn" *ngFor="let c of complexityOptions"
                        [class.active]="complexityKey === c.key"
                        [disabled]="isLocked"
                        (click)="complexityKey = c.key; recompute()">
                  {{ c.label }} <span class="factor-pill">x{{ c.factor }}</span>
                  <span class="info-badge" *ngIf="c.description" (click)="$event.stopPropagation()">i<span class="info-tip">{{ c.description }}</span></span>
                </button>
              </div>
            </div>

            <!-- Checkbox conditions -->
            <div class="form-group">
              <div class="cond-grid">
                <label class="cond-chip" [class.active]="restrictivePpe">
                  <input type="checkbox" [(ngModel)]="restrictivePpe" [disabled]="isLocked" (ngModelChange)="recompute()">
                  <span>Restrictive PPE</span>
                  <span class="factor-pill">x{{ toggleOptions[0].factor }}</span>
                  <span class="info-badge" *ngIf="toggleOptions[0].description" (click)="$event.stopPropagation()">i<span class="info-tip">{{ toggleOptions[0].description }}</span></span>
                </label>
                <label class="cond-chip" [class.active]="confinedSpace">
                  <input type="checkbox" [(ngModel)]="confinedSpace" [disabled]="isLocked" (ngModelChange)="recompute()">
                  <span>Confined Space</span>
                  <span class="factor-pill">x{{ toggleOptions[1].factor }}</span>
                  <span class="info-badge" *ngIf="toggleOptions[1].description" (click)="$event.stopPropagation()">i<span class="info-tip">{{ toggleOptions[1].description }}</span></span>
                </label>
              </div>
            </div>

            <!-- Elevated Work segmented selector -->
            <div class="form-group">
              <label class="form-label">Elevated Work</label>
              <div class="toggle-group">
                <button class="toggle-btn" *ngFor="let e of elevatedOptions"
                        [class.active]="elevatedWork === e.key"
                        [disabled]="isLocked"
                        (click)="elevatedWork = e.key; recompute()">
                  {{ e.label }} <span class="factor-pill">x{{ e.factor }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Submit -->
          <div class="submit-row">
            <button class="btn btn-primary" *ngIf="!isLocked" (click)="submit()" [disabled]="isLoading || !canSubmit()">
              <svg *ngIf="!isLoading" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398l3.85 3.85a1 1 0 0 0 1.415-1.415l-3.868-3.833zm-5.242 1.15a5 5 0 1 1 0-10 5 5 0 0 1 0 10z"/>
              </svg>
              <span *ngIf="isLoading" class="spinner"></span>
              {{ isLoading ? 'Searching...' : 'Search NORMs' }}
            </button>
            <button class="btn btn-outline-primary" *ngIf="isLocked" (click)="clearForm()">
              &#8635; New Task Lookup
            </button>
          </div>
        </section>

        <!-- RIGHT: Results panel -->
        <section class="results-panel">

          <!-- Idle state -->
          <div class="idle-state card" *ngIf="!result && !error && !isLoading">
            <div class="idle-icon"></div>
            <h3>Select a task category and fill in the parameters</h3>
            <p>Results will show the standard NORMs duration, crew size, and activity breakdown.</p>
            <div class="quick-picks">
              <span class="qp-heading">Jump to example:</span>
              <button class="qp-chip" (click)="jumpToCategory('motor', 'Disconnect &amp; Reconnect')">Motor D&amp;R</button>
              <button class="qp-chip" (click)="jumpToCategory('pump', 'PM')">Pump PM</button>
              <button class="qp-chip" (click)="jumpToCategory('valve', 'PM')">Control Valve PM</button>
              <button class="qp-chip" (click)="jumpToCategory('pipework', 'Flange Makeup')">Flange Makeup</button>
              <button class="qp-chip" (click)="jumpToCategory('instrumentation')">Instrumentation</button>
            </div>

            <!-- Planner guidance -->
            <div class="guidance-section">
              <div class="guidance-header" (click)="guidanceExpanded = !guidanceExpanded">
                <span class="guidance-title">&#9432; How to use NORMs in NEST</span>
                <span class="guidance-toggle">{{ guidanceExpanded ? '&#9650;' : '&#9660;' }}</span>
              </div>

              <div class="guidance-body" *ngIf="guidanceExpanded">

                <div class="guidance-block">
                  <div class="guidance-block-title">What are NORMs?</div>
                  <p>NORMs estimating uses data and standardized factors to provide consistent and accurate labor estimates, replacing guesswork with industry benchmarks and historical data. It ensures reliability and consistency across job plans — similar to following a recipe for precise results.</p>
                </div>

                <div class="guidance-block">
                  <div class="guidance-block-title">Two types of NORMs</div>
                  <div class="guidance-two-col">
                    <div class="guidance-type-card">
                      <div class="gtc-label">Quantity Based</div>
                      <p>Derived from <strong>Arcadis Industrial Norms</strong> table lookups. Each step of an activity is timed individually and summed to produce the activity duration. Used for tasks where scope can be measured (e.g. pipe size, valve type, tubing length).</p>
                    </div>
                    <div class="guidance-type-card">
                      <div class="gtc-label">Level of Effort</div>
                      <p>Based on <strong>Chevron historical averages</strong>, validated by craft and management SMEs. Used for routine maintenance tasks that don't have direct industry norms. NEST retrieves these via the generic lookup prompt.</p>
                    </div>
                  </div>
                </div>

                <div class="guidance-block">
                  <div class="guidance-block-title">Rounding philosophy</div>
                  <p>Durations in the <strong>Validated NORM Total Effort (hours)</strong> column represent the total task duration; standard headcount is in the <strong>Standard Resource Count</strong> column. After the level-of-effort or quantity-based duration is calculated, it is <strong>rounded up to the nearest half hour</strong>, then divided by the Standard Resource Count to enable planning and scheduling in 15-minute increments &mdash; shown in the <strong>Rounded NORM Effort (hours)</strong> column. Use that value when entering into Maximo.</p>
                </div>

                <div class="guidance-block">
                  <div class="guidance-block-title">Step-by-step: using NEST for RM creation</div>
                  <ol class="guidance-steps">
                    <li>Select the <strong>Task Category</strong> that best matches your work (Instrumentation, Electrical, Motor, Pump, PRD, Control Valve, etc.).</li>
                    <li>Fill in the parameters (keywords, size, pressure class, work type).</li>
                    <li>Apply <strong>Job Conditions</strong> (complexity, PPE, confined space, elevated work) if applicable.</li>
                    <li>Click <strong>Search NORMs</strong>. For tubing or conduit work, enter the <strong>length</strong> and/or number of <strong>bends</strong> in the Added Duration section — NEST will calculate and add the time automatically.</li>
                    <li>Review the returned <strong>NORM Duration</strong> and <strong>Crew Size</strong>.</li>
                    <li>Enter headcount and duration into the Maximo labor plan exactly as NEST recommends.</li>
                    <li>Type the work order number into the <strong>work order #</strong> field in NEST to unlock the reference code.</li>
                    <li>Copy the <strong>Reference Code</strong> using the automated button and paste it into the work notes of the Maximo task. This links the estimate back to the NORMs library for metrics and audits.</li>
                    <li>Flag the task as normalized.</li>
                  </ol>
                </div>

                <div class="guidance-block">
                  <div class="guidance-block-title">How NEST uses the NORMs Library</div>
                  <p>NEST cross-references line items from the NORMs Library using embedded logic and your planning cues, retrieving the corresponding activity and presenting it for review. Based on your inputs and the type of work, it applies <strong>multipliers or adders</strong> and includes a rolled-up task duration in the suggestion. Enter the headcount and duration into your Maximo task plan exactly as NEST recommends, then copy the unique <strong>Reference Code</strong> into the task's <em>work notes</em> so the estimate links back to the library for metric reference and audit.</p>
                </div>

                <div class="guidance-block">
                  <div class="guidance-block-title">Why this matters</div>
                  <ul class="guidance-bullets">
                    <li><strong>Cost savings</strong> — Consistent planning reduces subjectivity and increases workforce productivity.</li>
                    <li><strong>Efficiency</strong> — Standardization streamlines scoping for routine and improved maintenance planning.</li>
                    <li><strong>Enterprise standardization</strong> — A consistent estimating basis enables site-by-site productivity analysis and standard activity creation.</li>
                  </ul>
                </div>

              </div>
            </div>
          </div>

          <!-- Loading skeleton -->
          <div class="loading-card card" *ngIf="isLoading">
            <div class="skeleton-line wide"></div>
            <div class="skeleton-line medium"></div>
            <div class="skeleton-metrics">
              <div class="skeleton-metric"></div>
              <div class="skeleton-metric"></div>
            </div>
          </div>

          <!-- Error -->
          <div class="result-card card error-card" *ngIf="error">
            <div class="rc-alert rc-alert-error">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zm0 1A8 8 0 1 1 8 0a8 8 0 0 1 0 16z"/><path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/></svg>
              {{ error }}
            </div>
          </div>

          <!-- Result found -->
          <div class="result-card card" *ngIf="result && result.task && !error">

            <!-- Header row -->
            <div class="rc-head">
              <div class="rc-title-row">
                <h2 class="rc-task-name">{{ overrideNormHours != null ? lastQuery : result.task }}</h2>
                <span class="craft-tag" [attr.data-craft]="result.craft">{{ result.craft }}</span>
              </div>
              <div class="rc-badges">
                <span class="badge badge-info" *ngIf="result.hours_pending">Pending SME Validation</span>
                <span class="badge badge-success" *ngIf="!result.hours_pending">Validated</span>
                <span class="rc-query-text">Query: "{{ lastQuery }}"</span>
              </div>
            </div>

            <!-- Metrics row -->
            <div class="rc-metrics">
              <div class="metric-tile" [class.pending]="result.hours_pending">
                <span class="mt-icon mt-icon-text">hrs</span>
                <div class="mt-body">
                  <ng-container *ngIf="!result.hours_pending">
                    <div class="mt-value" *ngIf="!estimate">{{ overrideNormHours ?? result.norm_hours }} <span class="mt-unit">hrs</span></div>
                    <div class="mt-value mt-adjusted" *ngIf="estimate">
                      {{ estimate.hoursPerResource ?? estimate.adjustedHours }} <span class="mt-unit">hrs</span>
                      <span class="mt-lock" title="Locked — generated by NEST">&#128274;</span>
                    </div>
                  </ng-container>
                  <div class="mt-value pending" *ngIf="result.hours_pending">Pending</div>
                  <div class="mt-label" *ngIf="!estimate">NORM Duration</div>
                  <div class="mt-label" *ngIf="estimate">
                    Per Resource
                    <span class="mt-base-note" *ngIf="estimate.crewSize && estimate.crewSize > 1">({{ estimate.crewSize }} crew · total {{ estimate.adjustedHours }} h)</span>
                    <span class="mt-base-note" *ngIf="!estimate.crewSize || estimate.crewSize <= 1">(base: {{ overrideNormHours ?? result.norm_hours }} h)</span>
                  </div>
                </div>
              </div>
              <div class="metric-tile">
                <span class="mt-icon mt-icon-text">crew</span>
                <div class="mt-body">
                  <div class="mt-value">{{ result.norm_resource_count }}</div>
                  <div class="mt-label">Crew Size</div>
                </div>
              </div>
            </div>

            <!-- Pending notice -->
            <div class="rc-notice notice-warning" *ngIf="result.hours_pending">
              <strong>Hours not yet validated for this task.</strong>
              Contact the NORMs SME team, or use the <a routerLink="/labor-hours">Labor Hours</a> tab for a data-driven historical estimate.
            </div>

            <!-- Activity breakdown -->
            <div class="rc-section" *ngIf="getEmbeddedSteps().length > 0 || getActivityList().length > 0">
              <div class="rc-section-label">Activity Breakdown</div>
              <!-- Embedded steps (structured list from model) -->
              <div class="activity-tags" *ngIf="getEmbeddedSteps().length > 0">
                <span class="activity-tag" *ngFor="let s of getEmbeddedSteps()">{{ s }}</span>
              </div>
              <!-- Fallback: activity tags -->
              <div class="activity-tags" *ngIf="getEmbeddedSteps().length === 0">
                <span class="activity-tag" *ngFor="let a of getActivityList()">
                  {{ a.icon }} {{ a.label }}
                </span>
              </div>
            </div>

            <!-- Reference code + Maximo WO# (grouped, side by side) -->
            <div class="rc-section ref-section" *ngIf="estimate">
              <div class="rcwo-grid">
                <!-- Left: Maximo WO# (required — gates the Copy button) -->
                <div class="rcwo-col">
                  <label class="rc-section-label" for="maximoWo">
                    Maximo WO # <span class="wo-required">*</span>
                    <span class="info-badge">i<span class="info-tip">Required. Enter the Maximo work order this estimate is for — copying the reference code logs it against this WO for later reconciliation.</span></span>
                  </label>
                  <input id="maximoWo" class="wo-input" type="text" [(ngModel)]="maximoWoNumber"
                         (blur)="woTouched = true" [class.wo-invalid]="woTouched && !isWoValid()"
                         placeholder="e.g. 123456 or 1234567-12" autocomplete="off" inputmode="numeric" />
                  <span class="wo-hint" [class.wo-hint-error]="woTouched && !isWoValid()">
                    {{ (woTouched && !isWoValid()) ? 'Enter a valid WO # (6–7 digits, optionally -task).' : '6–7-digit WO #, optionally -task.' }}
                  </span>
                </div>
                <!-- Right: Reference Code -->
                <div class="rcwo-col">
                  <div class="rc-section-label">
                    Reference Code
                    <span class="info-badge">i<span class="info-tip">Paste this code into the Maximo task's work notes to trace it back to this NEST estimate.</span></span>
                  </div>
                  <div class="copy-row">
                    <div class="ref-input" aria-label="Reference code">{{ estimate.referenceCode }}</div>
                    <button class="btn btn-secondary" [class.btn-copied]="copied" (click)="copyReferenceCode()"
                            [disabled]="!isWoValid()"
                            [title]="isWoValid() ? 'Copy the reference code' : 'Enter a valid Maximo WO # to copy'">{{ copied ? '✓ Copied' : 'Copy' }}</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- ═══ Added Duration (tubing / conduit, NORMs results only) ═══ -->
            <div class="rc-section adjust-section" *ngIf="hasBaseHours && (isTubing() || isConduit())">
              <div class="rc-section-label">Added Duration</div>

              <!-- Tubing added duration -->
              <div class="form-group added-dur" *ngIf="isTubing()">
                <label class="form-label">Tubing (added duration)</label>
                <div class="three-col">
                  <div>
                    <span class="mini-label">Size</span>
                    <div class="size-locked-display">{{ tubing.size }}</div>
                  </div>
                  <div>
                    <span class="mini-label">Length (ft)</span>
                    <input type="number" min="0" step="1" [(ngModel)]="tubing.lengthFt" (ngModelChange)="recompute()">
                  </div>
                  <div>
                    <span class="mini-label"># Bends</span>
                    <input type="number" min="0" step="1" [(ngModel)]="tubing.bends" (ngModelChange)="recompute()">
                  </div>
                </div>
              </div>

              <!-- Conduit added duration -->
              <div class="form-group added-dur" *ngIf="isConduit()">
                <label class="form-label">Conduit (added duration)</label>
                <div>
                  <span class="mini-label">Estimated length (ft)</span>
                  <input type="number" min="0" step="1" [(ngModel)]="conduit.lengthFt" (ngModelChange)="recompute()">
                </div>
              </div>
            </div>

            <!-- ═══ Estimate + Maximo side-by-side ═══ -->
            <div class="estimate-maximo-grid" *ngIf="estimate">

              <!-- Left: Normalized Estimate breakdown -->
              <div class="rc-section breakdown">
                <div class="rc-section-label">Normalized Estimate</div>
                <div class="bd-rows">
                  <div class="bd-row">
                    <span>Base NORM effort</span>
                    <span>{{ estimate.baseHours }} h</span>
                  </div>
                  <div class="bd-row sub" *ngFor="let m of estimate.multipliers">
                    <span>{{ m.label }}</span>
                    <span>x{{ m.factor }}</span>
                  </div>
                  <div class="bd-row strong">
                    <span>Multiplier product</span>
                    <span>x{{ estimate.multiplierProduct }} = {{ (estimate.baseHours * estimate.multiplierProduct) | number:'1.0-2' }} h</span>
                  </div>
                  <div class="bd-row sub" *ngFor="let a of estimate.addedDurations">
                    <span>+ {{ a.label }}<span class="bd-detail">{{ a.detail }}</span></span>
                    <span>+{{ a.hours }} h</span>
                  </div>
                  <div class="bd-row total">
                    <span>Adjusted total (task)</span>
                    <span>{{ estimate.adjustedHours }} h</span>
                  </div>
                  <div class="bd-row total per-resource" *ngIf="estimate.crewSize && estimate.crewSize > 1">
                    <span>Per resource (÷ {{ estimate.crewSize }})</span>
                    <span>{{ estimate.hoursPerResource }} h</span>
                  </div>
                </div>
              </div>

              <!-- Right: Estimate Summary -->
              <div class="rc-section maximo-section">
                <div class="rc-section-label">Estimate Summary</div>
                <textarea class="maximo-box" rows="14" [value]="estimate.maximoDescription" readonly></textarea>
              </div>

            </div>

            <!-- Alternatives / similar tasks -->
            <ng-container *ngIf="getResultAlternatives() as alts">
              <div class="rc-section" *ngIf="alts.length > 0">
                <div class="rc-section-label">Similar Tasks</div>
                <div class="alt-list">
                  <button class="alt-row" *ngFor="let alt of alts" (click)="quickSearch(alt)">
                    <span>{{ alt }}</span>
                    <span class="alt-arrow">&rsaquo;</span>
                  </button>
                </div>
              </div>
            </ng-container>

            <!-- Actions -->
            <div class="rc-actions">
              <button class="btn btn-secondary" (click)="copyResult()">Copy Result</button>
              <button class="btn btn-secondary" *ngIf="result.hours_pending" (click)="flagTask()">Flag for SME Review</button>
              <button class="btn-link" (click)="result = null; error = ''">&larr; New Search</button>
            </div>
          </div>

          <!-- No match -->
          <div class="result-card card" *ngIf="result && !result.task && !error">
            <div class="rc-no-match">
              <div class="nm-icon">? ?</div>
              <h3>No exact match found</h3>
              <p>{{ result.hint || 'Try a different keyword, or browse these suggestions:' }}</p>
              <div class="alt-list" *ngIf="getSuggestions().length > 0">
                <button class="alt-row" *ngFor="let alt of getSuggestions()" (click)="quickSearch(alt)">
                  <span>{{ alt }}</span><span class="alt-arrow">&rsaquo;</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Recent searches -->
          <div class="recent-section" *ngIf="recentSearches.length > 0">
            <div class="recent-label">Recent Searches</div>
            <div class="recent-chips">
              <button class="recent-chip" *ngFor="let s of recentSearches" (click)="quickSearch(s)">
                {{ s }}
              </button>
            </div>
          </div>

        </section>
      </div>
    </div>
  `,
  styles: [`
    /*  Page shell  */
    .page-wrapper { max-width: 1280px; margin: 0 auto; }

    .page-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      margin-bottom: var(--spacing-lg);
    }
    .page-header h1 {
      font-size: 1.375rem;
      font-weight: 700;
      color: var(--cvx-blue);
      margin: 0;
    }
    .page-sub {
      font-size: 0.8125rem;
      color: var(--gray-500);
      margin-top: 0.25rem;
    }

    /*  Two-column grid  */
    .content-grid {
      display: grid;
      grid-template-columns: 360px 1fr;
      gap: var(--spacing-lg);
      align-items: start;
    }
    @media (max-width: 900px) {
      .content-grid { grid-template-columns: 1fr; }
    }

    /*  Left panel  */
    .params-panel {
      padding: var(--spacing-lg);
    }

    .panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: var(--spacing-lg);
    }
    .panel-title {
      font-size: 0.9375rem;
      font-weight: 700;
      color: var(--gray-900);
    }

    .form-group {
      margin-bottom: var(--spacing-md);
    }

    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: var(--spacing-md);
    }

    .form-hint-box {
      background: var(--cvx-light);
      border-left: 3px solid var(--cvx-mid);
      padding: 0.5rem 0.75rem;
      font-size: 0.75rem;
      color: var(--cvx-blue);
      border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
      margin-bottom: var(--spacing-md);
    }

    /* Category buttons */
    .category-group {
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;
    }

    /* 3-column category grid (I&E · Rotating · Fixed) */
    .category-columns {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0.375rem;
      align-items: start;
    }
    .category-col {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }
    .category-col .cat-btn {
      width: 100%;
      text-align: center;
    }

    /* Task quick-search */
    .task-search {
      position: relative;
      margin-bottom: 0.625rem;
    }
    .task-search-input {
      width: 100%;
      padding: 0.375rem 1.75rem 0.375rem 0.75rem;
      border: 1px solid var(--gray-300);
      border-radius: var(--radius-md);
      font-size: 0.8rem;
    }
    .task-search-input:focus {
      outline: none;
      border-color: var(--cvx-mid);
      box-shadow: 0 0 0 2px var(--cvx-light);
    }
    .task-search-clear {
      position: absolute;
      top: 0.25rem;
      right: 0.4rem;
      border: none;
      background: none;
      font-size: 1.1rem;
      line-height: 1;
      color: var(--gray-500);
      cursor: pointer;
      padding: 0.1rem 0.3rem;
    }
    .task-search-clear:hover { color: var(--cvx-blue); }
    .task-search-results {
      display: flex;
      flex-direction: column;
      max-height: 220px;
      overflow-y: auto;
      border: 1px solid var(--gray-200);
      border-radius: var(--radius-md);
      margin-top: 0.375rem;
      background: var(--white);
    }
    .task-search-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 0.5rem;
      padding: 0.4rem 0.625rem;
      border: none;
      border-bottom: 1px solid var(--gray-100);
      background: none;
      text-align: left;
      cursor: pointer;
      font-size: 0.78rem;
    }
    .task-search-item:last-child { border-bottom: none; }
    .task-search-item:hover { background: var(--cvx-light); }
    .tsi-sub { color: var(--gray-800); font-weight: 500; }
    .tsi-cat { color: var(--gray-500); font-size: 0.7rem; white-space: nowrap; }
    .task-search-empty {
      margin-top: 0.375rem;
      padding: 0.4rem 0.625rem;
      font-size: 0.75rem;
      color: var(--gray-500);
      font-style: italic;
    }
    .cat-btn {
      padding: 0.375rem 0.75rem;
      border: 1px solid var(--gray-300);
      border-radius: var(--radius-md);
      background: var(--white);
      color: var(--gray-700);
      font-size: 0.8rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s;
    }
    .cat-btn:hover { border-color: var(--cvx-mid); color: var(--cvx-mid); }
    .cat-btn.active {
      background: var(--cvx-blue);
      border-color: var(--cvx-blue);
      color: #fff;
      font-weight: 600;
    }
    .cat-btn-sm {
      padding: 0.25rem 0.625rem;
      font-size: 0.75rem;
    }

    /* flex-wrap modifier for toggle groups (sub-cat option fields) */
    .toggle-group.flex-wrap { flex-wrap: wrap; }

    /* Sub-form container */
    .sub-form {
      background: var(--gray-50);
      border: 1px solid var(--gray-200);
      border-radius: var(--radius-md);
      padding: var(--spacing-md);
      margin-bottom: var(--spacing-md);
    }
    .sub-form .form-group:last-child { margin-bottom: 0; }

    /* Query preview */
    .query-preview {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      background: var(--cvx-light);
      border-radius: var(--radius-md);
      padding: 0.5rem 0.75rem;
      margin-top: var(--spacing-sm);
      font-size: 0.75rem;
    }
    .qp-label { font-weight: 700; color: var(--cvx-blue); white-space: nowrap; }
    .qp-value { color: var(--gray-700); font-style: italic; }

    /* Job Conditions block (left panel) */
    .jc-section {
      border: 1px dashed var(--gray-300);
      border-radius: var(--radius-md);
      padding: var(--spacing-md);
      margin-bottom: var(--spacing-md);
      background: var(--gray-50);
    }
    .jc-section-label {
      font-size: 0.6875rem;
      text-transform: uppercase;
      letter-spacing: 0.07em;
      font-weight: 700;
      color: var(--gray-500);
      margin-bottom: var(--spacing-sm);
    }
    .jc-section .form-group:last-child { margin-bottom: 0; }

    .submit-row {
      padding-top: var(--spacing-md);
      border-top: 1px solid var(--gray-200);
    }
    .submit-row .btn { width: 100%; justify-content: center; padding: 0.5625rem; font-size: 0.875rem; }

    /* Spinner */
    .spinner {
      width: 14px; height: 14px;
      border: 2px solid rgba(255,255,255,0.4);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    /*  Right panel  */
    .results-panel { display: flex; flex-direction: column; gap: var(--spacing-md); }

    /* Idle state */
    .idle-state {
      padding: var(--spacing-xl);
      text-align: center;
    }
    .idle-icon { font-size: 2.5rem; margin-bottom: var(--spacing-md); }
    .idle-state h3 { font-size: 1rem; font-weight: 600; margin-bottom: var(--spacing-sm); color: var(--gray-700); }
    .idle-state p { font-size: 0.875rem; color: var(--gray-500); }

    .quick-picks {
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;
      justify-content: center;
      margin-top: var(--spacing-lg);
      align-items: center;
    }
    .qp-heading { font-size: 0.75rem; color: var(--gray-500); margin-right: 0.25rem; }
    .qp-chip {
      background: var(--gray-100);
      border: 1px solid var(--gray-200);
      border-radius: 9999px;
      padding: 0.25rem 0.75rem;
      font-size: 0.75rem;
      cursor: pointer;
      transition: all 0.15s;
    }
    .qp-chip:hover { background: var(--cvx-light); border-color: var(--cvx-mid); color: var(--cvx-blue); }

    /* Planner guidance accordion */
    .guidance-section {
      margin-top: var(--spacing-lg);
      border: 1px solid var(--gray-200);
      border-radius: var(--radius-md);
      text-align: left;
      overflow: hidden;
    }
    .guidance-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.625rem 1rem;
      background: var(--gray-50);
      cursor: pointer;
      user-select: none;
      border-bottom: 1px solid transparent;
      transition: background 0.15s;
    }
    .guidance-header:hover { background: var(--cvx-light); }
    .guidance-title { font-size: 0.8125rem; font-weight: 600; color: var(--cvx-blue); }
    .guidance-toggle { font-size: 0.625rem; color: var(--gray-400); }
    .guidance-body {
      padding: 1rem 1.25rem;
      display: flex;
      flex-direction: column;
      gap: 1rem;
      border-top: 1px solid var(--gray-200);
    }
    .guidance-block-title {
      font-size: 0.75rem;
      font-weight: 700;
      color: var(--cvx-blue);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 0.375rem;
    }
    .guidance-block p,
    .guidance-block li {
      font-size: 0.8125rem;
      color: var(--gray-600);
      line-height: 1.55;
      margin: 0;
    }
    .guidance-two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;
    }
    .guidance-type-card {
      background: var(--gray-50);
      border: 1px solid var(--gray-200);
      border-radius: var(--radius-sm);
      padding: 0.75rem;
    }
    .gtc-label {
      font-size: 0.75rem;
      font-weight: 700;
      color: var(--gray-700);
      margin-bottom: 0.375rem;
    }
    .guidance-steps, .guidance-bullets {
      padding-left: 1.25rem;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    /* Loading skeleton */
    .loading-card { padding: var(--spacing-lg); }
    .skeleton-line { height: 14px; background: var(--gray-200); border-radius: 3px; margin-bottom: 0.75rem; animation: pulse 1.2s ease-in-out infinite; }
    .skeleton-line.wide { width: 70%; }
    .skeleton-line.medium { width: 45%; }
    .skeleton-metrics { display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md); margin-top: var(--spacing-lg); }
    .skeleton-metric { height: 72px; background: var(--gray-200); border-radius: var(--radius-md); animation: pulse 1.2s ease-in-out infinite; }
    @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.45} }

    /* Result card */
    .result-card { padding: var(--spacing-lg); }
    .error-card .rc-alert { display: flex; align-items: center; gap: 0.625rem; font-size: 0.875rem; }
    .rc-alert-error { color: var(--danger-red); }

    /* Result header */
    .rc-head { margin-bottom: var(--spacing-lg); }
    .rc-title-row { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.375rem; }
    .rc-task-name { font-size: 1.125rem; font-weight: 700; color: var(--gray-900); margin: 0; }
    .rc-badges { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
    .rc-query-text { font-size: 0.75rem; color: var(--gray-500); font-style: italic; margin-left: auto; }

    /* Craft tag */
    .craft-tag {
      padding: 0.1875rem 0.625rem;
      border-radius: var(--radius-sm);
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.04em;
    }
    .craft-tag[data-craft="INST"] { background: #DBEAFE; color: #1D4ED8; }
    .craft-tag[data-craft="MACH"] { background: #DCFCE7; color: #15803D; }
    .craft-tag[data-craft="ELEC"] { background: #FEF3C7; color: #B45309; }
    .craft-tag[data-craft="PFBM"] { background: #F3E8FF; color: #7C3AED; }

    /* Metrics */
    .rc-metrics {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: var(--spacing-md);
      margin-bottom: var(--spacing-lg);
    }
    .metric-tile {
      display: flex;
      align-items: center;
      gap: 0.875rem;
      padding: var(--spacing-md) var(--spacing-lg);
      background: var(--gray-50);
      border: 1px solid var(--gray-200);
      border-radius: var(--radius-md);
    }
    .metric-tile.pending { background: #FFF7ED; border-color: #FED7AA; }
    .mt-icon { font-size: 1.75rem; }
    .mt-value { font-size: 1.625rem; font-weight: 700; color: var(--cvx-blue); line-height: 1.2; }
    .mt-value.pending { font-size: 1.125rem; color: #92400E; font-style: italic; }
    .mt-unit { font-size: 0.875rem; font-weight: 400; color: var(--gray-500); }
    .mt-label { font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.06em; color: var(--gray-500); margin-top: 0.125rem; }

    /* Notices */
    .rc-notice {
      padding: 0.75rem var(--spacing-md);
      border-radius: var(--radius-md);
      font-size: 0.8125rem;
      line-height: 1.5;
      margin-bottom: var(--spacing-lg);
    }
    .notice-warning { background: #FFF7ED; border-left: 3px solid #F97316; color: #7C2D12; }
    .notice-warning a { color: var(--cvx-mid); }

    /* Activity / section */
    .rc-section { margin-bottom: var(--spacing-md); }
    .rc-section-label {
      font-size: 0.6875rem;
      text-transform: uppercase;
      letter-spacing: 0.07em;
      font-weight: 700;
      color: var(--gray-500);
      margin-bottom: 0.5rem;
    }
    .activity-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;
      background: var(--cvx-light);
      border: 1px solid #BFDBFE;
      border-radius: var(--radius-md);
      padding: 0.4375rem 0.75rem;
    }
    .activity-tag {
      background: transparent;
      color: var(--cvx-blue);
      border-radius: var(--radius-sm);
      padding: 0.25rem 0.625rem;
      font-size: 0.75rem;
      font-weight: 500;
    }

    /* Alternatives */
    .alt-list { display: flex; flex-direction: column; gap: 2px; }
    .alt-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.4375rem 0.75rem;
      border: 1px solid var(--gray-200);
      border-radius: var(--radius-md);
      background: var(--white);
      font-size: 0.8125rem;
      cursor: pointer;
      text-align: left;
      transition: all 0.15s;
    }
    .alt-row:hover { background: var(--cvx-light); border-color: var(--cvx-mid); }
    .alt-arrow { color: var(--cvx-mid); font-size: 1.125rem; }

    /* Actions */
    .rc-actions {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      padding-top: var(--spacing-md);
      border-top: 1px solid var(--gray-200);
    }

    /* No match */
    .rc-no-match { text-align: center; padding: var(--spacing-xl) var(--spacing-md); }
    .nm-icon { font-size: 2.5rem; margin-bottom: var(--spacing-md); }
    .rc-no-match h3 { font-size: 1rem; font-weight: 600; margin-bottom: var(--spacing-sm); }
    .rc-no-match p { font-size: 0.875rem; color: var(--gray-500); margin-bottom: var(--spacing-lg); }
    .rc-no-match .alt-list { text-align: left; max-width: 360px; margin: 0 auto; }

    /* ── Adjustment factors ── */
    .adjust-section { padding-top: var(--spacing-md); }
    .adjust-note {
      font-size: 0.75rem; color: var(--gray-500); line-height: 1.45;
      background: var(--cvx-light); border-radius: var(--radius-md);
      padding: 0.5rem 0.75rem; margin-bottom: var(--spacing-md);
    }
    .factor-pill {
      font-size: 0.625rem; font-weight: 700; color: var(--cvx-mid);
      background: var(--white); border-radius: 9999px; padding: 0.05rem 0.35rem;
      margin-left: 0.25rem; border: 1px solid var(--gray-200);
    }
    .toggle-btn.active .factor-pill { color: var(--cvx-blue); }
    .cond-grid { display: flex; flex-wrap: wrap; gap: 0.5rem; }
    .cond-chip {
      display: inline-flex; align-items: center; gap: 0.4rem;
      border: 1px solid var(--gray-300); border-radius: var(--radius-md);
      padding: 0.375rem 0.625rem; font-size: 0.8rem; cursor: pointer;
      background: var(--white); transition: all 0.15s; position: relative;
    }
    .cond-chip.active { border-color: var(--cvx-blue); background: var(--cvx-light); color: var(--cvx-blue); font-weight: 600; }
    .cond-chip input { cursor: pointer; }
    /* ── Info badge + tooltip ── */
    .info-badge {
      position: relative;
      display: inline-flex; align-items: center; justify-content: center;
      width: 16px; height: 16px; flex-shrink: 0;
      border-radius: 50%; border: 1.5px solid var(--gray-400);
      font-size: 0.75rem; font-weight: 700; font-style: italic;
      color: var(--gray-400); line-height: 1;
      transition: color 0.15s, border-color 0.15s;
      vertical-align: middle;
    }
    .info-badge:hover { color: var(--cvx-blue); border-color: var(--cvx-blue); }
    .info-tip {
      visibility: hidden; opacity: 0;
      position: absolute; bottom: calc(100% + 7px); left: 50%;
      transform: translateX(-50%);
      background: #1e293b; color: #fff;
      font-size: 0.7rem; font-weight: 400; font-style: normal;
      padding: 0.35rem 0.55rem;
      border-radius: var(--radius-sm, 4px);
      white-space: normal; pointer-events: none; z-index: 300;
      min-width: 140px; max-width: 210px;
      text-align: center; line-height: 1.45;
      transition: opacity 0.15s;
    }
    .info-tip::after {
      content: ''; position: absolute; top: 100%; left: 50%;
      transform: translateX(-50%);
      border: 5px solid transparent; border-top-color: #1e293b;
    }
    .info-badge:hover .info-tip { visibility: visible; opacity: 1; }
    .pending-dot { width: 7px; height: 7px; border-radius: 50%; background: #F97316; display: inline-block; }
    .added-dur .three-col { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0.5rem; }
    .mini-label { display: block; font-size: 0.6875rem; color: var(--gray-500); margin-bottom: 0.2rem; }

    /* ── Breakdown ── */
    .breakdown { }
    .bd-rows {
      border: 1px solid var(--gray-200);
      border-radius: var(--radius-md);
      overflow: hidden;
      margin-bottom: var(--spacing-md);
      flex: 1;
    }
    .bd-row {
      display: flex; justify-content: space-between; align-items: baseline; gap: 1rem;
      padding: 0.4375rem 0.75rem; font-size: 0.8125rem; border-bottom: 1px solid var(--gray-100);
    }
    .bd-row:last-child { border-bottom: none; }
    .bd-row.sub { color: var(--gray-600); padding-left: 1.25rem; font-size: 0.78rem; }
    .bd-row.strong { font-weight: 600; background: var(--gray-50); }
    .bd-row.total { font-weight: 700; color: var(--cvx-blue); background: var(--cvx-light); font-size: 0.9375rem; }
    .bd-row.per-resource { color: #15803D; background: #f0fdf4; }
    .bd-detail { display: block; font-size: 0.6875rem; color: var(--gray-400); font-style: italic; white-space: pre-wrap; }
    .pending-tag {
      font-size: 0.625rem; font-weight: 700; color: #B45309; background: #FEF3C7;
      border-radius: 9999px; padding: 0.05rem 0.4rem; margin-left: 0.35rem; text-transform: uppercase;
    }
    .locked-duration {
      display: flex; align-items: center; gap: 0.75rem;
      background: #F0FDF4; border: 1px solid #BBF7D0; border-radius: var(--radius-md);
      padding: 0.75rem var(--spacing-md); margin-bottom: var(--spacing-md);
    }
    .lock-icon { font-size: 1.5rem; }
    .ld-value { font-size: 1.375rem; font-weight: 700; color: #15803D; line-height: 1.1; }
    .ld-unit { font-size: 0.75rem; font-weight: 400; color: var(--gray-500); }
    .ld-note { font-size: 0.6875rem; color: var(--gray-600); margin-top: 0.15rem; }
    .placeholder-warn {
      font-size: 0.75rem; color: #7C2D12; background: #FFF7ED;
      border-left: 3px solid #F97316; border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
      padding: 0.5rem 0.75rem; margin-bottom: var(--spacing-md);
    }
    .copy-row { display: flex; gap: 0.5rem; align-items: center; margin-top: 0.35rem; }

    /* Panel lock */
    .panel-locked-notice {
      font-size: 0.75rem; color: #92400E; background: #FFFBEB;
      border: 1px solid #FDE68A; border-radius: var(--radius-sm);
      padding: 0.375rem 0.75rem; margin-bottom: var(--spacing-md);
    }
    .new-task-btn { font-weight: 600; color: var(--cvx-blue); }
    .jc-locked { opacity: 0.55; pointer-events: none; }
    .btn-outline-primary {
      width: 100%; padding: 0.625rem; border-radius: var(--radius-md);
      border: 2px solid var(--cvx-blue); background: transparent;
      color: var(--cvx-blue); font-weight: 600; font-size: 0.875rem;
      cursor: pointer; transition: all 0.15s;
    }
    .btn-outline-primary.btn-sm {
      width: auto; padding: 0.25rem 0.75rem; font-size: 0.8rem;
      border-width: 1.5px; border-radius: var(--radius-md);
    }
    .btn-outline-primary:hover { background: var(--cvx-light); }

    /* Adjusted duration in metric tile */
    .mt-adjusted { color: #15803D !important; }
    .mt-lock { font-size: 0.875rem; margin-left: 0.25rem; }
    .mt-base-note { font-size: 0.6rem; color: var(--gray-400); font-weight: 400; margin-left: 0.25rem; }

    /* Reference code section (above estimate) */
    .ref-section { padding-bottom: 0; margin-bottom: var(--spacing-sm); }

    /* Side-by-side Estimate + Maximo */
    .estimate-maximo-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: var(--spacing-md);
      margin-top: var(--spacing-md);
      align-items: start;
    }
    @media (max-width: 1100px) {
      .estimate-maximo-grid { grid-template-columns: 1fr; }
    }
    .breakdown, .maximo-section {
      display: flex;
      flex-direction: column;
    }
    .maximo-section .maximo-box { width: 100%; box-sizing: border-box; flex: 1; }

    /* Embedded steps */
    .embedded-steps { display: flex; flex-direction: column; gap: 0.375rem; }
    .step-row { display: flex; align-items: baseline; gap: 0.5rem; font-size: 0.8125rem; }
    .step-num {
      flex-shrink: 0; width: 1.25rem; height: 1.25rem; border-radius: 50%;
      background: var(--cvx-blue); color: #fff;
      font-size: 0.625rem; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
    }
    .step-text { color: var(--gray-700); line-height: 1.4; }

    /* Size locked display */
    .size-locked-display {
      background: var(--gray-100); border: 1px solid var(--gray-200);
      border-radius: var(--radius-sm); padding: 0.375rem 0.625rem;
      font-size: 0.875rem; font-weight: 600; color: var(--gray-700);
      min-height: 2rem; display: flex; align-items: center;
    }
    .ref-input {
      flex: 1; min-width: 0; font-family: monospace; font-size: 0.8125rem; color: var(--gray-700);
      background: var(--gray-50); border: 1px solid var(--gray-200);
      border-radius: var(--radius-md); padding: 0.4375rem 0.75rem;
      line-height: 1.5; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      /* Non-selectable: users must click Copy (which logs the estimate) to grab the code. */
      user-select: none; -webkit-user-select: none; cursor: default;
    }
    .wo-row { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.5rem; }
    .wo-label { font-size: 0.75rem; color: var(--gray-600); white-space: nowrap; }
    .wo-optional { color: var(--gray-400); font-weight: 400; }
    .wo-required { color: #dc2626; font-weight: 700; }
    .wo-hint { display: block; font-size: 0.6875rem; color: var(--gray-500); margin-top: 0.25rem; }
    .wo-hint-error { color: #dc2626; }
    .wo-input.wo-invalid { border-color: #dc2626; }
    .btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
    .btn-copied, .btn-copied:hover { background: #16a34a; border-color: #16a34a; color: #fff; }
    .wo-input {
      flex: 1; max-width: 180px; font-size: 0.8125rem; color: var(--gray-700);
      background: var(--white); border: 1px solid var(--gray-200);
      border-radius: var(--radius-md); padding: 0.4375rem 0.75rem;
    }
    /* Reference Code + Maximo WO# side-by-side */
    /* Same 1fr 1fr columns + gap as .estimate-maximo-grid below, so the
       Reference Code column lines up vertically with Maximo Long-Description. */
    .rcwo-grid {
      display: grid; grid-template-columns: 1fr 1fr;
      gap: var(--spacing-md); align-items: start;
    }
    @media (max-width: 640px) { .rcwo-grid { grid-template-columns: 1fr; } }
    .rcwo-col { display: flex; flex-direction: column; }
    .rcwo-col .rc-section-label { display: flex; align-items: center; gap: 0.3rem; }
    .rcwo-col .rc-section-label .info-badge,
    .rcwo-col .rc-section-label .info-tip { text-transform: none; }
    .rcwo-col .copy-row { margin-top: 0; }
    .rcwo-col .wo-input {
      max-width: none; width: 100%; box-sizing: border-box; margin-top: 0.05rem;
    }
    .maximo-box {
      width: 100%; font-family: monospace; font-size: 0.72rem; line-height: 1.4;
      color: var(--gray-700); background: var(--gray-50);
      border: 1px solid var(--gray-200); border-radius: var(--radius-md);
      padding: 0.625rem; resize: vertical;
    }

    /* Recent */
    .recent-section { }
    .recent-label { font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.07em; font-weight: 700; color: var(--gray-500); margin-bottom: 0.5rem; }
    .recent-chips { display: flex; flex-wrap: wrap; gap: 0.375rem; }
    .recent-chip {
      background: var(--white);
      border: 1px solid var(--gray-200);
      border-radius: 9999px;
      padding: 0.25rem 0.75rem;
      font-size: 0.75rem;
      cursor: pointer;
      transition: all 0.15s;
    }
    .recent-chip:hover { border-color: var(--cvx-mid); color: var(--cvx-mid); }

    /* ── Pump/Motor sizing reference guide ── */
    .sizing-guide { margin-bottom: var(--spacing-sm); }
    .sizing-guide-toggle {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.375rem 0.75rem;
      background: var(--cvx-light);
      border: 1px solid var(--cvx-mid);
      border-radius: var(--radius-md);
      color: var(--cvx-blue);
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.15s;
    }
    .sizing-guide-toggle:hover { background: #BFDBFE; }
    .sizing-toggle-arrow { font-size: 0.6rem; color: var(--gray-400); }
    .sizing-guide-body { margin-top: 0.375rem; overflow-x: auto; }
    .sizing-guide-note {
      font-size: 0.7rem; color: var(--gray-500); margin: 0.2rem 0 0.375rem; font-style: italic;
    }
    .sizing-table { width: 100%; border-collapse: collapse; font-size: 0.72rem; }
    .sizing-table { table-layout: fixed; }
    .sizing-table th {
      background: var(--cvx-blue); color: #fff;
      padding: 0.25rem 0.3rem; text-align: left; font-weight: 600; white-space: nowrap;
    }
    .sizing-table td { padding: 0.2rem 0.3rem; border: 1px solid var(--gray-200); white-space: nowrap; }
    .sizing-table th:first-child, .sizing-table td:first-child { width: 19%; }
    .sz-small  td:first-child { background: #DCFCE7; color: #15803D; font-weight: 700; }
    .sz-medium td:first-child { background: #FEF3C7; color: #B45309; font-weight: 700; }
    .sz-large  td:first-child { background: #DBEAFE; color: #1D4ED8; font-weight: 700; }
    .sz-xlarge td:first-child { background: #F3E8FF; color: #7C3AED; font-weight: 700; }
  `]
})
export class NormsLookupComponent implements OnInit {
  private normsService = inject(NormsService);
  private estimation = inject(EstimationService);
  private appInsights = inject(AppInsightsService);
  private cdr = inject(ChangeDetectorRef);
  private normsCache = inject(NormsCacheService);

  // Falls back to this if /api/norms-meta hasn't resolved (or has no `updated` value) yet.
  private static readonly FALLBACK_UPDATED_LABEL = 'June 2026';

  get libraryUpdatedLabel(): string {
    const updated = this.normsCache.meta?.updated;
    if (!updated) return NormsLookupComponent.FALLBACK_UPDATED_LABEL;
    const parsed = new Date(updated);
    if (isNaN(parsed.getTime())) return updated;
    return parsed.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  }

  // Taxonomy-driven categories
  readonly taxCategories = TASK_TAXONOMY;
  selectedSubCat = '';
  subCatOptions: Record<string, string> = {};

  // Quick task search (filters every sub-task across all categories)
  taskSearch = '';

  // Filters
  craft: CraftFilter = 'ALL';
  category: TaskCategory = 'motor';

  // Sub-form models
  general  = { query: '' };
  prd: PrdForm  = { size: '', action: '', pressureClass: '' };
  orifice: OrificeForm = { pipeSize: '', pressureClass: '' };
  pump: PumpScreenForm = { pipeSize: '', pressureClass: '' };

  // State
  isLoading   = false;
  isLocked    = false;
  result: NormsLookupResponse | null = null;

  // Required Maximo WO# captured at estimate time; gates the Copy button (which logs the estimate).
  maximoWoNumber = '';
  woTouched = false;
  // 6–7-digit WO#, optional -task suffix. Hyphen optional until the Maximo table mapping is confirmed.
  private readonly woPattern = /^\d{6,7}(-\d{1,4})?$/;
  // Transient “Copied” confirmation on the reference-code button.
  copied = false;
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;
  error       = '';
  lastQuery   = '';
  recentSearches: string[] = [];

  // ── Adjustment factors (NORMs Lookup tab ONLY — historical/labor-hours
  //    results already include "fat" and must never receive multipliers) ──
  readonly complexityOptions = JOB_COMPLEXITY;
  readonly toggleOptions = TOGGLE_MULTIPLIERS;
  readonly elevatedOptions = ELEVATED_WORK_OPTIONS;
  complexityKey = 'standard';
  restrictivePpe = false;
  confinedSpace = false;
  elevatedWork = 'lt20';

  // Added-duration inputs (tubing / conduit)
  readonly tubingSizes = OPTION_LISTS.tubingSizes;
  readonly conduitSizes = OPTION_LISTS.conduitSizes;
  tubing = { size: OPTION_LISTS.tubingSizes[0] ?? '1/4"', lengthFt: 0, bends: 0 };
  conduit = { lengthFt: 0 };

  // Size dropdown state (for exact-match tubing/conduit queries)
  detectedType: 'tubing' | 'conduit' | null = null;
  showSizeDropdown = false;
  selectedSize = '';
  // SME-confirmed override: set when user picks via size dropdown so fuzzy ML
  // match errors cannot corrupt the base norm hours.
  overrideNormHours: number | null = null;
  guidanceExpanded = false;
  // SME reference chart toggle (motor / pump size guide)
  showSizingChart = false;

  // Computed estimate (canonical record)
  estimate: NormsEstimate | null = null;

  //  Lookup option arrays (data-driven — see estimation-config.ts) 
  readonly prdSizes        = OPTION_LISTS.prdSizes;
  readonly prdActions      = OPTION_LISTS.prdActions;
  pressureClasses: string[] = [];   // updated by setCategory()
  readonly pipeSizes       = OPTION_LISTS.pipeSizes;
  readonly pumpSizes       = OPTION_LISTS.pumpSizes;

  ngOnInit(): void {
    // Demo readiness: warm the Function proxy + ML endpoint so the first
    // real lookup is fast.
    this.normsService.prewarm();
  }

  private readonly prdActionQueryMap: Record<string, string> = {
    removal: 'Removal', install: 'Install', swap: 'swap'
  };

  buildPrdQuery(): string {
    if (!this.prd.size || !this.prd.action || !this.prd.pressureClass) return '';
    const [inlet, outlet] = this.prd.size.split('x');
    const sizeLabel = `${inlet}" x ${outlet}"`;
    const actionLabel = this.prdActionQueryMap[this.prd.action] ?? this.prd.action;
    return `${sizeLabel} PRD ${actionLabel} (${this.prd.pressureClass})`;
  }

  private isCombinedAction(alt: string): boolean {
    return /removal\/install|install\/removal/i.test(alt);
  }

  getResultAlternatives(): string[] {
    if (this.result?.alternatives && this.result.alternatives.length > 0) {
      return this.result.alternatives.filter(a => !this.isCombinedAction(a));
    }
    // Fallback for PRD: mirrors the API pattern seen for swap queries:
    // 1. Other actions for the same size + pressure class
    // 2. Same action for all other sizes + same pressure class
    if (this.category === 'prd' && this.prd.size && this.prd.action && this.prd.pressureClass) {
      const [inlet, outlet] = this.prd.size.split('x');
      const currentSizeLabel = `${inlet}" x ${outlet}"`;
      const currentActionLabel = this.prdActionQueryMap[this.prd.action];
      const alts: string[] = [];

      // 1. Other actions, same size + pressure class
      for (const action of this.prdActions) {
        if (action !== this.prd.action) {
          alts.push(`${currentSizeLabel} PRD ${this.prdActionQueryMap[action]} (${this.prd.pressureClass})`);
        }
      }

      // 2. Same action, other sizes, same pressure class
      for (const size of this.prdSizes) {
        if (size !== this.prd.size) {
          const [i, o] = size.split('x');
          alts.push(`${i}" x ${o}" PRD ${currentActionLabel} (${this.prd.pressureClass})`);
        }
      }

      return alts;
    }
    return [];
  }

  buildOrificeQuery(): string {
    if (!this.orifice.pipeSize || !this.orifice.pressureClass) return '';
    return `Orifice Replacement PM ${this.orifice.pipeSize} x ${this.orifice.pressureClass}`;
  }

  buildPumpQuery(): string {
    if (!this.pump.pipeSize || !this.pump.pressureClass) return '';
    return `Pump suction screen cleaning - ${this.pump.pipeSize} x ${this.pump.pressureClass}`;
  }

  //  Form helpers 
  setCategory(cat: TaskCategory): void {
    this.category = cat;
    this.result   = null;
    this.error    = '';
    this.estimate = null;
    this.selectedSubCat  = '';
    this.subCatOptions   = {};
    this.showSizingChart = false;
    this.pressureClasses = PRESSURE_CLASS_MAP[cat] ?? [];
    const first = this.pressureClasses[0] ?? '';
    this.prd.pressureClass     = first;
    this.orifice.pressureClass = first;
    this.pump.pressureClass    = first;
  }

  // ── Taxonomy helpers ──────────────────────────────────────────────────────

  isGenericCategory(): boolean {
    return !['general', 'prd'].includes(this.category);
  }

  getActiveTaxCat(): TaxCategory | null {
    return this.taxCategories.find(c => c.key === this.category) ?? null;
  }

  getActiveSubCatDef(): TaxSubCat | null {
    return this.getActiveTaxCat()?.subCats.find(s => s.label === this.selectedSubCat) ?? null;
  }

  /** True when the active subcat has a size ('sz') field — triggers the sizing guide. */
  showsSizeField(): boolean {
    return !!this.getActiveSubCatDef()?.fields.some(f => f.key === 'sz');
  }

  setSubCat(label: string): void {
    this.selectedSubCat  = label;
    this.subCatOptions   = {};
    // Auto-expand sizing guide when the selected sub-category has a size field
    this.showSizingChart = this.showsSizeField();
  }

  jumpToCategory(cat: TaskCategory, subCat?: string): void {
    this.setCategory(cat);
    if (subCat) this.setSubCat(subCat);
  }

  /** Matching sub-tasks across every category for the quick-search box (max 12). */
  searchResults(): Array<{ catKey: TaskCategory; catLabel: string; subLabel: string }> {
    const q = this.taskSearch.trim().toLowerCase();
    if (q.length < 2) return [];
    const out: Array<{ catKey: TaskCategory; catLabel: string; subLabel: string }> = [];
    for (const cat of this.taxCategories) {
      const catMatch = cat.label.toLowerCase().includes(q);
      for (const sc of cat.subCats) {
        if (catMatch || sc.label.toLowerCase().includes(q)) {
          out.push({ catKey: cat.key as TaskCategory, catLabel: cat.label, subLabel: sc.label });
          if (out.length >= 12) return out;
        }
      }
    }
    return out;
  }

  /** Jump to the category + sub-task chosen from the search results, then clear the box. */
  selectSearchResult(r: { catKey: TaskCategory; subLabel: string }): void {
    if (this.isLocked) return;
    this.jumpToCategory(r.catKey, r.subLabel);
    this.taskSearch = '';
  }

  setSubCatOption(key: string, value: string): void {
    this.subCatOptions = { ...this.subCatOptions, [key]: value };
  }

  buildGenericQuery(): string {
    const subCat = this.getActiveSubCatDef();
    if (!subCat) return '';
    // Only build query when all required fields are filled
    const allRequiredFilled = subCat.fields.filter(f => f.required).every(f => !!this.subCatOptions[f.key]);
    if (!allRequiredFilled) return '';
    return subCat.queryFn(this.subCatOptions);
  }

  clearForm(): void {
    this.prd      = { size: '', action: '', pressureClass: '' };
    this.orifice  = { pipeSize: '', pressureClass: '' };
    this.pump     = { pipeSize: '', pressureClass: '' };
    this.result   = null;
    this.error    = '';
    this.estimate = null;
    this.isLocked = false;
    this.overrideNormHours = null;
    this.selectedSubCat  = '';
    this.subCatOptions   = {};
    this.taskSearch = '';
    this.detectedType = null;
    this.showSizeDropdown = false;
    this.selectedSize = '';
    this.resetFactors();
  }

  /** Reset all adjustment factors + added-duration inputs to defaults. */
  resetFactors(): void {
    this.complexityKey  = 'standard';
    this.restrictivePpe = false;
    this.confinedSpace  = false;
    this.elevatedWork   = 'none';
    this.tubing  = { size: this.tubingSizes[0] ?? '1/4"', lengthFt: 0, bends: 0 };
    this.conduit = { lengthFt: 0 };
  }

  canSubmit(): boolean {
    switch (this.category) {
      case 'prd': return !!this.buildPrdQuery();
      default:    return !!this.buildGenericQuery();
    }
  }

  getActiveQuery(): string {
    switch (this.category) {
      case 'prd': return this.buildPrdQuery();
      default:    return this.buildGenericQuery();
    }
  }

  // ── Size-selector dropdown (tubing/conduit exact-match queries) ───────────

  onQueryChange(): void {
    const q = this.general.query.toLowerCase();
    
    // Check for tubing keywords
    const tubingKeywords = ['tubing', 'tube run', 'instrument tubing'];
    if (tubingKeywords.some(kw => q.includes(kw))) {
      this.detectedType = 'tubing';
      this.showSizeDropdown = true;
      this.selectedSize = '';
      return;
    }

    // Check for conduit keywords
    if (q.includes('conduit')) {
      this.detectedType = 'conduit';
      this.showSizeDropdown = true;
      this.selectedSize = '';
      return;
    }

    // No match — user is typing free-text, clear any previous override
    this.detectedType = null;
    this.showSizeDropdown = false;
    this.selectedSize = '';
    this.overrideNormHours = null;
  }

  /** Called when size dropdown changes — builds the query but does NOT submit.
   *  User must click "Search NORMs" to execute (same pattern as PRD/Orifice). */
  onSizeSelect(size: string): void {
    if (!this.detectedType || !size) return;

    if (this.detectedType === 'tubing') {
      const tier = TUBING_TIERS.find(t => t.size === size);
      const interval = tier?.intervalFt === 2 ? 'per 2\'' : 'per foot';
      this.general.query = `SS Tubing and fittings fabrication - ${size}; ${interval}`;
      this.overrideNormHours = tier?.normHours ?? null;
      this.tubing.size = size;
    } else if (this.detectedType === 'conduit') {
      this.general.query = `Aluminum Conduit surface installation - ${size}; per foot`;
      this.overrideNormHours = null;
    }
  }

  //  Actions 
  submit(): void {
    const query = this.getActiveQuery();
    if (!query) return;
    // Wire tubing SME-confirmed hours override for taxonomy path
    if (this.category === 'instrumentation' && this.selectedSubCat === 'Tubing') {
      const tier = TUBING_TIERS.find(t => t.size === this.subCatOptions['sz']);
      this.overrideNormHours = tier?.normHours ?? null;
      this.tubing.size = this.subCatOptions['sz'] ?? this.tubing.size;
    } else {
      this.overrideNormHours = null;
    }
    this.runQuery(query);
  }

  quickSearch(query: string): void {
    this.runQuery(query);
  }

  private runQuery(query: string): void {
    this.isLoading = true;
    this.error     = '';
    this.result    = null;
    this.estimate  = null;
    this.lastQuery = query;

    this.normsService.lookupNormsTask(query).subscribe({
      next: (response) => {
        this.result    = response;
        this.isLoading = false;
        this.isLocked  = true;
        this.recompute();
        this.cdr.detectChanges();
        if (query && !this.recentSearches.includes(query)) {
          this.recentSearches.unshift(query);
          if (this.recentSearches.length > 8) this.recentSearches.pop();
        }
      },
      error: (err) => {
        console.error('[NEST] error:', err);
        this.error     = err.message || 'Failed to reach the NEST endpoint.';
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  // ── Estimate adjustment (NORMs Lookup tab only) ──────────────────────────

  /** True when the matched task has a usable, validated base duration. */
  get hasBaseHours(): boolean {
    return !!this.result?.task && !this.result?.hours_pending
      && typeof this.result?.norm_hours === 'number' && (this.result?.norm_hours ?? 0) > 0;
  }

  isTubing(): boolean { return this.estimation.isTubingTask(this.result?.task); }
  isConduit(): boolean { return this.estimation.isConduitTask(this.result?.task); }

  /** Recompute the canonical estimate whenever a factor/input changes. */
  recompute(): void {
    if (!this.result?.task || !this.hasBaseHours) {
      this.estimate = null;
      return;
    }
    // Use SME-confirmed override when available (prevents ML fuzzy-match from
    // corrupting norm_hours — e.g. 3/4" query returning 3/8" data at 0.19h).
    const effectiveNormHours = this.overrideNormHours ?? this.result.norm_hours ?? 0;
    // Pick up API-provided bend rate for the selected tubing size (if API returns it).
    const apiTubingRate = this.result.tubing_unit_rates?.find(
      r => r.size === this.tubing?.size
    );
    this.estimate = this.estimation.computeEstimate({
      taskId: this.result.task_id ?? '',
      taskName: this.overrideNormHours != null ? this.lastQuery : this.result.task,
      craft: this.result.craft ?? '',
      crewSize: this.result.norm_resource_count ?? null,
      baseHours: effectiveNormHours,
      roundedBaseHours: effectiveNormHours,
      complexityKey: this.complexityKey,
      restrictivePpe: this.restrictivePpe,
      confinedSpace: this.confinedSpace,
      elevatedWork: this.elevatedWork,
      normHours: effectiveNormHours,
      tubing: this.isTubing() ? {
        ...this.tubing,
        bendRateOverride: apiTubingRate?.bend_unit_hours,
        bendIdOverride: apiTubingRate?.bend_id,
      } : null,
      conduit: this.isConduit() ? this.conduit : null,
    });
    // Stub hook: mark this job as normalized for the future KPI dashboard.
    this.normsService.recordNormalizedJob(
      this.estimate.referenceCode, this.estimate.taskId, this.estimate.taskName, this.estimate.adjustedHours);
    this.cdr.detectChanges();
  }

  isWoValid(): boolean {
    return this.woPattern.test(this.maximoWoNumber.trim());
  }

  copyReferenceCode(): void {
    if (!this.estimate) return;
    if (!this.isWoValid()) { this.woTouched = true; return; }
    navigator.clipboard.writeText(this.estimate.referenceCode);
    // Show a transient confirmation (NoopNgZone: the timeout must trigger CD itself).
    this.copied = true;
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
    this.copiedTimer = setTimeout(() => { this.copied = false; this.cdr.detectChanges(); }, 2000);
    // Copying the reference code is the finalize action: record the estimate for the metrics log.
    const e = this.estimate;
    this.appInsights.trackEvent('EstimateGenerated', {
      referenceCode: e.referenceCode,
      taskId: e.taskId,
      taskName: e.taskName,
      craft: e.craft,
      crewSize: String(e.crewSize ?? ''),
      baseHours: String(e.baseHours),
      multiplierProduct: String(e.multiplierProduct),
      adjustedHours: String(e.adjustedHours),
      hoursPerResource: String(e.hoursPerResource ?? ''),
    });
    // Also persist to the trusted metrics log (Azure SQL) for later Maximo reconciliation.
    this.normsService.logEstimate({
      referenceCode: e.referenceCode,
      taskId: e.taskId,
      taskName: e.taskName,
      craft: e.craft,
      crewSize: e.crewSize ?? null,
      baseHours: e.baseHours,
      multiplierProduct: e.multiplierProduct,
      adjustedHours: e.adjustedHours,
      rawAdjustedHours: e.rawAdjustedHours,
      hoursPerResource: e.hoursPerResource ?? null,
      rawHoursPerResource: e.rawHoursPerResource ?? null,
      maximoWoNumber: this.maximoWoNumber.trim() || null,
    });
  }

  copyMaximoDescription(): void {
    if (this.estimate) navigator.clipboard.writeText(this.estimate.maximoDescription);
  }

  getSuggestions(): string[] {
    if (!this.result) return [];
    return this.result.suggestions ?? this.result.alternatives ?? [];
  }

  getActivityList(): Array<{ label: string; icon: string }> {
    if (!this.result?.activity_breakdown) return [];
    return this.result.activity_breakdown
      .split('\n')
      .filter(line => line.trim().startsWith('\u2022'))
      .map(line => ({ label: line.replace(/^\s*\u2022\s*/, '').trim(), icon: '' }));
  }

  getEmbeddedSteps(): string[] {
    if (!this.result?.embedded_tasks) return [];
    return this.result.embedded_tasks
      .split('\n')
      .map(s => s.replace(/^\s*[\u2022\-\d\.]+\s*/, '').trim())
      .filter(s => s.length > 0);
  }

  copyResult(): void {
    if (!this.result) return;
    const lines = [
      `Task:   ${this.result.task}`,
      `Craft:  ${this.result.craft}`,
      `Hours:  ${this.result.hours_pending ? 'Pending SME validation' : this.result.norm_hours + ' hrs'}`,
      `Crew:   ${this.result.norm_resource_count}`,
    ];
    navigator.clipboard.writeText(lines.join('\n'));
  }

  flagTask(): void {
    if (this.result) {
      alert(`"${this.result.task}" flagged for SME validation queue.`);
    }
  }
}


